import * as THREE from "three";

import { SceneManager } from "../../../core/scene_manager.js";

export class EscalatorGauge
{
  private static readonly TRACK_WIDTH_SHARE = 0.6;
  private static readonly TRACK_MARGIN_SHARE = 0.25;
  private static readonly FILL_INSET_SHARE = 0.18;
  private static readonly TRACK_LIFT = 0.004;
  private static readonly FILL_LIFT = 0.008;
  private static readonly MIN_SCALE = 0.0001;
  private static readonly TRACK_COLOR = 0x2b2833;
  private static readonly FILL_LOW_COLOR = 0xffc83d;
  private static readonly FILL_HIGH_COLOR = 0xe0302b;

  private static readonly LOW = new THREE.Color( EscalatorGauge.FILL_LOW_COLOR );
  private static readonly HIGH = new THREE.Color( EscalatorGauge.FILL_HIGH_COLOR );

  private readonly meshes: THREE.Mesh[] = [];
  private readonly fill: THREE.Mesh;
  private readonly fill_material: THREE.MeshBasicMaterial;

  constructor( center_x: number, center_y: number, face_z: number, face_w: number, face_h: number )
  {
    const margin = face_w * EscalatorGauge.TRACK_MARGIN_SHARE;
    const track_w = face_w * EscalatorGauge.TRACK_WIDTH_SHARE;
    const track_h = face_h - margin * 2;
    const track_z = face_z + EscalatorGauge.TRACK_LIFT;

    const track_material = new THREE.MeshBasicMaterial( { color: EscalatorGauge.TRACK_COLOR } );
    const track = this.addMesh( new THREE.PlaneGeometry( track_w, track_h ), track_material );
    track.position.set( center_x, center_y, track_z );

    const inset = track_w * EscalatorGauge.FILL_INSET_SHARE;
    const fill_w = track_w - inset * 2;
    const fill_h = track_h - inset * 2;
    const fill_bottom_y = center_y - fill_h * 0.5;
    const fill_z = face_z + EscalatorGauge.FILL_LIFT;

    this.fill_material = new THREE.MeshBasicMaterial( { color: EscalatorGauge.FILL_LOW_COLOR } );
    this.fill = this.addMesh( new THREE.PlaneGeometry( fill_w, fill_h ).translate( 0, fill_h * 0.5, 0 ), this.fill_material );
    this.fill.position.set( center_x, fill_bottom_y, fill_z );

    this.setProgress( 0 );
  }

  public setProgress( value: number ): void
  {
    this.fill.visible = value > 0;
    this.fill.scale.y = Math.max( value, EscalatorGauge.MIN_SCALE );
    this.fill_material.color.lerpColors( EscalatorGauge.LOW, EscalatorGauge.HIGH, value );
  }

  public dispose(): void
  {
    for ( const mesh of this.meshes )
    {
      SceneManager.world.remove( mesh );
      mesh.geometry.dispose();
      ( mesh.material as THREE.Material ).dispose();
    }

    this.meshes.length = 0;
  }

  private addMesh( geometry: THREE.BufferGeometry, material: THREE.Material ): THREE.Mesh
  {
    const mesh = new THREE.Mesh( geometry, material );

    SceneManager.world.add( mesh );
    this.meshes.push( mesh );

    return mesh;
  }
}
